import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Modal } from '@/components/molecules';
import { ProductForm } from '@/components/organisms';
import { AttachmentList } from '@/components/organisms/Attachment/AttachmentList';
import { useProductsList } from '@/hooks/useErpApi';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: products, isLoading, error, refetch } = useProductsList();
  const product = products?.find((p) => p.id === id);
  // Variants are now preloaded with the product
  const variants = product?.variants || [];

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="CircleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">Failed to Load Product</h3>
              <p className="text-error-700 mb-3">{error?.message || 'Product not found'}</p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate('/product-inventory')}
                >
                  <Icon name="ArrowLeft" size={16} className="mr-2" />
                  Back to Products
                </Button>
                <Button variant="primary" size="sm" onClick={() => refetch()}>
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => navigate('/product-inventory')}>
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">{product.name}</h1>
            <p className="text-neutral-600 mt-1">Product Details</p>
          </div>
        </div>
        <Button variant="primary" onClick={() => setIsEditModalOpen(true)}>
          <Icon name="Pencil" size={16} className="mr-2" />
          Edit Product
        </Button>
      </div>

      {/* Product Information */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Product Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-neutral-600">Product Name</label>
            <p className="text-base text-neutral-900 mt-1">{product.name}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-600">Product ID</label>
            <p className="text-base text-neutral-900 mt-1 font-mono text-sm">{product.id}</p>
          </div>
          {product.description && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-neutral-600">Description</label>
              <p className="text-base text-neutral-900 mt-1">{product.description}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-neutral-600">Created At</label>
            <p className="text-base text-neutral-900 mt-1">
              {new Date(product.created_at).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-600">Last Updated</label>
            <p className="text-base text-neutral-900 mt-1">
              {new Date(product.updated_at).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Product Variants */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-neutral-900">Product Variants</h2>
          {variants.length > 0 && (
            <Badge variant="primary">
              {variants.length} variant{variants.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {variants.length > 0 ? (
          <div className="space-y-4">
            {variants.map((variant) => (
              <div key={variant.id} className="border border-neutral-200 rounded-lg p-4">
                {/* Variant Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Layers" size={20} className="text-primary-600" />
                    <span className="font-semibold text-neutral-900">{variant.brand_name}</span>
                    <span className="text-neutral-500">|</span>
                    <span className="text-sm text-neutral-600 font-mono">
                      {variant.sku || 'No SKU'}
                    </span>
                  </div>
                  <Badge variant={variant.is_active ? 'success' : 'error'} size="sm">
                    {variant.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {/* Variant Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-medium text-neutral-600">HSN Code</label>
                    <p className="text-sm text-neutral-900 mt-1">{variant.hsn_code || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600">Pack Size</label>
                    <p className="text-sm text-neutral-900 mt-1">{variant.pack_size}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600">Quantity</label>
                    <p className="text-sm text-neutral-900 mt-1">{variant.quantity}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600">GST Rate</label>
                    <p className="text-sm text-neutral-900 mt-1">
                      {variant.gst_rate ? `${variant.gst_rate}%` : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Variant Images - show preloaded image_urls and allow management via AttachmentList */}
                <div className="border-t border-neutral-200 pt-4">
                  <h4 className="text-sm font-medium text-neutral-700 mb-2">Variant Images</h4>

                  {/* Show preloaded images from image_urls */}
                  {variant.image_urls && variant.image_urls.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                      {variant.image_urls.map((url, index) => (
                        <div
                          key={index}
                          className="aspect-square bg-neutral-100 rounded-lg overflow-hidden"
                        >
                          <img
                            src={url}
                            alt={`${variant.variant_name} image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* AttachmentList for upload/delete - uses entity_type="variant" for auto-update */}
                  <AttachmentList
                    entityType="variant"
                    entityId={variant.id}
                    allowUpload={true}
                    allowDelete={true}
                    viewMode="grid"
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="Package" size={48} className="text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-600">No variants found for this product</p>
            <p className="text-neutral-500 text-sm mt-1">
              Variants can be added from the Product Inventory page
            </p>
          </div>
        )}
      </div>

      {/* Edit Product Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Product"
        size="lg"
      >
        <ProductForm
          initialData={product}
          onSuccess={handleEditSuccess}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
